/* eslint-disable global-require */
import React from 'react';
import { render } from 'react-dom';
import { compose, createStore, applyMiddleware } from 'redux';
import { persistStore, autoRehydrate, createTransform } from 'redux-persist';
import { Provider } from 'react-redux';
// import { composeWithDevTools } from 'redux-devtools-extension';
import { Router, hashHistory } from 'react-router';
import { syncHistoryWithStore, routerMiddleware } from 'react-router-redux';
import _map from 'lodash/map';
// Mui used fonts
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
// Custom
import { storePersistor } from 'actions';
import { ToastProvider } from 'components/toast/Toast';
// Locale
import ConnectedIntlProvider from './intl/ConnectedIntlProvider';
// Store config
import reducers from './reducers';
import persistPostProcess from './middlewares/persistPostProcess';
import actionsChaining from './middlewares/actionsChaining';
import apiService from './middlewares/apiService';
import apiServicePostProcess from './middlewares/apiServicePostProcess';
import webSocketPreProcess from './middlewares/webSocketPreProcess';
import webSocketService from './middlewares/webSocketService';
import webSocketPostProcess from './middlewares/webSocketPostProcess';
/* eslint-disable import/named */
import { getUsedRoutes, getCpmManagerValue, getCpmClientValue } from './models/product';
/* eslint-enable import/named */

const PRODUCT = __PRODUCT__; // eslint-disable-line no-undef

const SetTransform = createTransform(
  // No transformation while persisting
  null,
  // Reinit 'loading' to false to avoid infinite loading display
  // in Login component on page refresh
  (outboundState) => ({
    ...outboundState,
    loading: false,
  }),
  // Only apply to 'auth' reducer
  { whitelist: ['auth'] },
);

function configureStore() {
  const middleware = routerMiddleware(hashHistory);

  return new Promise((resolve, reject) => {
    try {
      const store = createStore(
        reducers,
        undefined,
        compose(
          applyMiddleware(
            middleware,
            actionsChaining,
            persistPostProcess,
            webSocketPreProcess,
            webSocketService,
            webSocketPostProcess,
            apiService,
            apiServicePostProcess,
          ),
          autoRehydrate(),
          // composeWithDevTools(),
        ),
      );

      // Begin periodically persisting the store
      const persistor = persistStore(
        store,
        {
          whitelist: [
            'version',
            'intl',
            'auth',
            'operator',
            'pageSize',
          ],
          transforms: [SetTransform],
        },
        () => resolve(store),
      );

      // Store persistor in a reducer to be able to use it in persistPostProcess middleware
      store.dispatch(storePersistor(persistor));
    } catch (e) {
      reject(e);
    }
  });
}

async function init() {
  // Wait for store configuration finished
  // i.e. when persist rehydratation is complete
  const store = await configureStore();

  // Create an enhanced history that syncs navigation events with the store
  const history = syncHistoryWithStore(hashHistory, store);

  function scrollToTop() {
    window.scrollTo(0, 0);
  }

  /* eslint-disable import/no-dynamic-require */
  const usedRoutes = _map(getUsedRoutes(PRODUCT), (path) => require(`./routes/${path}/index.js`));
  /* eslint-enable import/no-dynamic-require */

  let component;
  if (PRODUCT === getCpmManagerValue || PRODUCT === getCpmClientValue) {
    component = require('./containers/AppCpm');
  } else {
    component = require('./containers/App');
  }

  // Alternative approach without wildcards
  const rootRoute = {
    childRoutes: [
      {
        path: '/',
        component,
        indexRoute: { onEnter: (nextState, replace) => replace('/login') },
        childRoutes: [
          // Catch-all matcher for any path that starts with /app/
          // Was an issue with OPAC previously load with /#/app useless prefix.
          // We now redirect to ensure URLs work for users with bookmarks or URL generate by other support services.
          {
            path: 'app/*',
            onEnter: (nextState, replace) => {
              // Get the full path from location
              const fullPath = nextState.location.pathname;

              // Extract the part after /app
              const newPath = fullPath.replace(/^\/app/, '') || '/';

              // Redirect to the new path WITH query parameters
              replace({
                pathname: newPath,
                query: nextState.location.query,
              });
            },
          },
          ...usedRoutes,
          {
            path: '*',
            indexRoute: { onEnter: (nextState, replace) => replace('/404') },
          },
        ],
      },
    ],
  };

  render(
    <Provider store={store}>
      <ConnectedIntlProvider>
        <ToastProvider>
          <Router onUpdate={scrollToTop} history={history} routes={rootRoute} />
        </ToastProvider>
      </ConnectedIntlProvider>
    </Provider>,
    document.getElementById('root'),
  );
}

init();



// WEBPACK FOOTER //
// ./client.js